/* MiniGameHost.js — Task #246
 * ---------------------------------------------------------------------------
 * Mounts ONE AI-invented mini-game inside a hardened sandboxed iframe and is the
 * ONLY bridge between that untrusted game and the lesson stage.
 *
 * Isolation (defense in depth):
 *   - iframe sandbox = "allow-scripts" ONLY (no allow-same-origin => null origin,
 *     no DOM/cookie/storage access to our page),
 *   - srcdoc carries a strict CSP: default-src 'none' (no network at all),
 *     style/script 'unsafe-inline' so the game can run, img/font data: only,
 *   - all host<->game traffic is postMessage carrying a per-mount NONCE, and we
 *     accept messages ONLY from THIS iframe's contentWindow with the right nonce.
 *
 * Trust: the game NEVER decides correctness. It reports its state; the SERVER
 * grades with the stored checker (the answer never reaches the browser). The
 * host relays semantic events (drag_active / long_think / answer_correct / …) to
 * the stage via onEvent so the reaction mapper can make the legend react.
 */

export class MiniGameHost {
  constructor(opts) {
    opts = opts || {};
    this.mountEl = opts.mount || null;
    this.game = opts.game || {};
    this.key = opts.key || this.game.key || "";
    this.authHeader = typeof opts.authHeader === "function" ? opts.authHeader : () => ({});
    this.onEvent = typeof opts.onEvent === "function" ? opts.onEvent : () => {};
    this.captureMode = opts.captureMode === true;
    const captureTimeoutMs = Number(opts.captureTimeoutMs);
    this.captureTimeoutMs = Number.isFinite(captureTimeoutMs) && captureTimeoutMs > 0
      ? captureTimeoutMs : 6000;
    this.nonce = "g" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    this.iframe = null;
    this._ready = false;
    this._lastState = null;
    this._stateWaiters = [];
    this._reqSeq = 0;
    this._onMessage = this._onMessage.bind(this);
    this._destroyed = false;
  }

  /* Build the iframe + wire the message channel. */
  mount() {
    if (!this.mountEl) return;
    window.addEventListener("message", this._onMessage);
    const iframe = document.createElement("iframe");
    iframe.className = "ts-game-frame";
    iframe.setAttribute("sandbox", "allow-scripts");
    iframe.setAttribute("title", this.game.title || "Interactive activity");
    iframe.setAttribute("scrolling", "no");
    iframe.style.cssText =
      "width:100%;border:0;display:block;background:transparent;min-height:220px;";
    iframe.srcdoc = this._buildSrcDoc();
    this.mountEl.appendChild(iframe);
    this.iframe = iframe;
  }

  _buildSrcDoc() {
    const csp =
      "default-src 'none'; " +
      "style-src 'unsafe-inline'; " +
      "script-src 'unsafe-inline'; " +
      "img-src data:; font-src data:; " +
      "form-action 'none'; base-uri 'none';";
    const css = String(this.game.css || "");
    const html = String(this.game.html || "");
    const js = String(this.game.js || "");
    const bridge = this._bridgeScript();
    // Game JS runs AFTER the bridge so window.LegendGame already exists. We wrap
    // it so a throw in the game can't abort the bridge.
    return (
      "<!doctype html><html><head><meta charset=\"utf-8\">" +
      '<meta http-equiv="Content-Security-Policy" content="' + csp + '">' +
      '<meta name="viewport" content="width=device-width, initial-scale=1">' +
      "<style>" + BASE_CSS + "\n" + css + "</style></head><body>" +
      html +
      "<scr" + "ipt>" + bridge + "</scr" + "ipt>" +
      "<scr" + "ipt>try{\n" + js + "\n}catch(e){}</scr" + "ipt>" +
      "</body></html>"
    );
  }

  /* The in-iframe shim that exposes window.LegendGame and speaks our nonce. */
  _bridgeScript() {
    return (
      "(function(){var N=" + JSON.stringify(this.nonce) + ";" +
      "var inits=[],reveals=[],resets=[],results=[],getState=null;" +
      "function send(t,p){try{parent.postMessage({source:'legendgame',nonce:N,type:t,payload:p||{}}, '*');}catch(e){}}" +
      "window.LegendGame={" +
      "ready:function(){send('ready');}," +
      "reportState:function(s){send('state',{state:s});}," +
      "activity:function(k){send('activity',{kind:String(k||'interact')});}," +
      "onInit:function(f){if(typeof f==='function')inits.push(f);}," +
      "onReveal:function(f){if(typeof f==='function')reveals.push(f);}," +
      "onReset:function(f){if(typeof f==='function')resets.push(f);}," +
      "onResult:function(f){if(typeof f==='function')results.push(f);}," +
      "onGetState:function(f){if(typeof f==='function')getState=f;}};" +
      "window.addEventListener('message',function(e){var d=e.data;" +
      "if(!d||d.source!=='legendhost'||d.nonce!==N)return;" +
      "if(d.type==='init'){inits.forEach(function(f){try{f(d.payload&&d.payload.init);}catch(_){}});}" +
      "else if(d.type==='reveal'){reveals.forEach(function(f){try{f(d.payload&&d.payload.solution);}catch(_){}});}" +
      "else if(d.type==='reset'){resets.forEach(function(f){try{f();}catch(_){}});}" +
      "else if(d.type==='result'){results.forEach(function(f){try{f(d.payload||{});}catch(_){}});}" +
      "else if(d.type==='getState'){var s=null;if(getState){try{s=getState();}catch(_){}}send('state',{state:s,reqId:d.payload&&d.payload.reqId});}" +
      "});" +
      "send('booted');})();"
    );
  }

  _post(type, payload) {
    if (!this.iframe || !this.iframe.contentWindow) return;
    try {
      this.iframe.contentWindow.postMessage(
        { source: "legendhost", nonce: this.nonce, type, payload: payload || {} },
        "*"
      );
    } catch (_) {}
  }

  _onMessage(e) {
    if (this._destroyed) return;
    // Accept ONLY messages from THIS iframe's window, with our nonce.
    if (!this.iframe || e.source !== this.iframe.contentWindow) return;
    const d = e.data;
    if (!d || d.source !== "legendgame" || d.nonce !== this.nonce) return;
    const p = d.payload || {};
    switch (d.type) {
      case "booted":
        this._post("init", { init: this.game.init || {} });
        break;
      case "ready":
        this._ready = true;
        this.onEvent("board_shown");
        break;
      case "activity": {
        const k = String(p.kind || "interact");
        if (k === "drag_active") this.onEvent("drag_active");
        else if (k === "long_think") this.onEvent("long_think");
        else this.onEvent("student_working");
        break;
      }
      case "state":
        this._lastState = p.state;
        if (this._stateWaiters.length) {
          const waiters = this._stateWaiters;
          this._stateWaiters = [];
          waiters.forEach((w) => {
            if (p.reqId == null || p.reqId === w.reqId) w.resolve(p.state);
            else this._stateWaiters.push(w);
          });
        }
        break;
      default:
        break;
    }
  }

  /* Ask the game for its current state (fresh round-trip, with a fallback). */
  _requestState(timeoutMs) {
    return new Promise((resolve) => {
      const reqId = ++this._reqSeq;
      let done = false;
      const waiter = {
        reqId,
        resolve: (s) => {
          if (done) return;
          done = true;
          resolve(s);
        },
      };
      this._stateWaiters.push(waiter);
      this._post("getState", { reqId });
      setTimeout(() => {
        if (done) return;
        done = true;
        this._stateWaiters = this._stateWaiters.filter((w) => w !== waiter);
        resolve(this._lastState);
      }, timeoutMs || 600);
    });
  }

  /* Task #524 — the tutor-chat context bridge reads the game's state.
   * lastKnownState() is synchronous (whatever the game last reported);
   * requestState() forces a fresh round-trip so an ask mid-activity sees the
   * board as it is now (falls back to the last known copy on timeout). */
  lastKnownState() {
    return this._lastState;
  }

  requestState(timeoutMs) {
    return this._requestState(timeoutMs);
  }

  /* Grade the current answer on the SERVER. Returns {correct, error?}. */
  async checkAnswer() {
    const state = await this._requestState();
    let result = { correct: false };
    try {
      const request = (async () => {
        const res = await fetch(
          "/api/learn/minigame/" + encodeURIComponent(this.key) + "/grade",
          {
          method: "POST",
          headers: Object.assign({ "Content-Type": "application/json" }, this.authHeader()),
          credentials: "same-origin",
          body: JSON.stringify({ state, capture: this.captureMode }),
          }
        );
        const data = await res.json();
        return { res, data };
      })();
      let response;
      if (this.captureMode) {
        let timer = null;
        try {
          response = await Promise.race([
            request,
            new Promise((_, reject) => {
              timer = setTimeout(
                () => reject(new Error("capture grade timed out")), this.captureTimeoutMs);
            }),
          ]);
        } finally {
          if (timer) clearTimeout(timer);
        }
      } else {
        response = await request;
      }
      const { res, data } = response;
      result = {
        correct: !!(data && data.correct),
        error: !res.ok || !data || typeof data.correct !== "boolean"
          ? "unavailable" : data.error,
      };
    } catch (e) {
      result = { correct: false, error: "network" };
    }
    if (this._destroyed) return { correct: false, error: "cancelled" };
    if (!result.error) {
      this._post("result", { correct: result.correct });
      this.onEvent(result.correct ? "answer_correct" : "answer_wrong");
    }
    return result;
  }

  /* Reveal the answer (server returns the solution state; the game arranges it). */
  async reveal() {
    // Capture is verdict-only. Never ask a server-held answer to cross the
    // sandbox boundary during a recording take.
    if (this.captureMode) return false;
    let solution = null;
    try {
      const res = await fetch(
        "/api/learn/minigame/" + encodeURIComponent(this.key) + "/reveal",
        {
          method: "POST",
          headers: Object.assign({ "Content-Type": "application/json" }, this.authHeader()),
          credentials: "same-origin",
          body: JSON.stringify({ capture: this.captureMode }),
        }
      );
      const data = await res.json();
      solution = data && data.solution;
    } catch (_) {}
    this._post("reveal", { solution });
    this.onEvent("answer_revealed");
    return true;
  }

  reset() {
    this._post("reset");
  }

  destroy() {
    this._destroyed = true;
    window.removeEventListener("message", this._onMessage);
    this._stateWaiters = [];
    if (this.iframe && this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
    }
    this.iframe = null;
  }
}

/* Minimal, neutral reset so games look at home in the stage card without each
 * game having to re-specify the basics. The game's own CSS follows and wins. */
const BASE_CSS =
  "*{box-sizing:border-box}" +
  "html,body{margin:0;padding:0;background:transparent;" +
  "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;" +
  "color:#2b2622;-webkit-font-smoothing:antialiased}" +
  "body{padding:4px}" +
  "button{font:inherit;cursor:pointer}" +
  "img{max-width:100%}";

export default MiniGameHost;
